import { SWITCHED_OVER } from "../release";
/**
 * The public switchboard line (DESIGN.md §3) for EVERY public address at once: the home page and its menu pages, Welcome, sign-in,
 * forgot / reset, registration (all steps), the status pages and Page not found. "legacy" = today's look. The owner flips this one line.
 * The look is the only thing that changes; sagas, APIs, step order and business rules are the same on both sides.
 */
export type PublicMode = "legacy" | "next";
export const PUBLIC_MODE: PublicMode = SWITCHED_OVER ? "next" : "legacy"; // the switch-over (next/release.ts)

/** Home page "wow" (boards 313/314): W1 cloud bank, W2 morning sky. The owner has not picked yet; both are built. */
export type HomeVariant = "W1" | "W2";
export const HOME_VARIANT: HomeVariant = "W2"; // owner picked W2 (board 340, v5)

const read = (key: string): string | null => {
  try { return window.sessionStorage.getItem(key); } catch { return null; }
};
/** Preview aids exist only outside a production build, so a visitor can never open the unreleased look. */
const preview = process.env.NODE_ENV !== "production";

/**
 * Soft launch (preview mode): the secret link `?preview=<code>` (or `#preview=<code>`, which never reaches a server) (code set on the server, PREVIEW_PUBLIC_CODE, rotatable) puts a cookie in THIS
 * browser; with the cookie the new public pages are served on the real addresses, with `noindex` while it lasts. The way out is a small text link in the public footer and, inside the app, an account-menu item
 * (never a floating banner: on a phone it covered buttons). `?preview=off` (or the link) removes the cookie. The cookie holds the code, which the server re-checks at every start-up, so a
 * rotated code ends every old cookie.
 */
const COOKIE = "cy_preview";
/** "Show me the old version": the reverse link `?legacy=<code>` (PREVIEW_LEGACY_CODE) keeps TODAY's public pages in this browser after the release. */
const LEGACY_COOKIE = "cy_legacy";
const getCookie = (name: string = COOKIE): string | null => {
  try {
    const m = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
    return m ? decodeURIComponent(m.slice(name.length + 1)) : null;
  } catch { return null; }
};
const setCookie = (value: string | null, name: string = COOKIE): void => {
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = value === null
      ? `${name}=; Max-Age=0; Path=/; SameSite=Lax${secure}`
      : `${name}=${encodeURIComponent(value)}; Max-Age=${60 * 60 * 24 * 90}; Path=/; SameSite=Lax${secure}`;
  } catch { /* cookies blocked: the constant rules */ }
};
/** This browser holds the old-public-pages link (the old look, whatever PUBLIC_MODE says). */
export const hasPublicLegacyCookie = (): boolean => getCookie(LEGACY_COOKIE) !== null;
export const hasPublicPreviewCookie = (): boolean => getCookie() !== null;
/** The code this browser holds from the secret link (sent with sign-up so the new account starts in the new design), or null. */
export const publicPreviewCode = (): string | null => getCookie();
/** The way out: removes the cookie and reloads on today's look. */
export const turnOffPublicPreview = (): void => { setCookie(null); window.location.assign("/"); };

/** true = the current code, false = the server said it is not, null = no clear answer (busy, offline, error): keep what the browser has. */
const checkCode = async (code: string, path: string = "/api/preview/public"): Promise<boolean | null> => {
  try {
    const base = process.env.REACT_APP_API_URL ?? "";
    // POST body, never the address: the code stays out of access logs and error reports.
    const r = await fetch(`${base}${path}`, {
      method: "POST", credentials: "omit", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ code }),
    });
    if (!r.ok) return null;
    const valid = (await r.json()).valid;
    return valid === true ? true : valid === false ? false : null;
  } catch { return null; }
};

const addNoIndex = (): void => {
  if (document.querySelector('meta[name="robots"][data-cy-preview]')) return;
  const m = document.createElement("meta");
  m.name = "robots"; m.content = "noindex, nofollow"; m.setAttribute("data-cy-preview", "1");
  document.head.appendChild(m);
};

/** Called once at start-up (every build): handles `?preview=`, re-checks the cookie, marks the page noindex (the way out is a footer link / account-menu item). */
export const bootPublicPreview = (search: string, reload: () => void = () => window.location.replace(window.location.pathname), hash: string = ""): void => {
  try {
    // The link may carry the code in the address (?preview=) or, better, after the # (never sent to any server). Either way the address
    // is cleaned at once, before anything is requested, so the code is not left in the history or in a copied link.
    const fromHash = new URLSearchParams(hash.replace(/^#/, ""));
    // The reverse link (after the release): `?legacy=<code>` keeps the old public pages in this browser; `?legacy=off` ends it.
    const lq = new URLSearchParams(search).get("legacy") ?? fromHash.get("legacy");
    if (lq !== null) {
      try { window.history.replaceState(null, "", window.location.pathname); } catch { /* the reload below cleans it */ }
      if (lq === "off") { setCookie(null, LEGACY_COOKIE); reload(); return; }
      void checkCode(lq, "/api/preview/legacy").then((ok) => { if (ok === true) setCookie(lq, LEGACY_COOKIE); reload(); });
      return;
    }
    const legacyCode = getCookie(LEGACY_COOKIE);
    if (legacyCode !== null) {
      addNoIndex();
      void checkCode(legacyCode, "/api/preview/legacy").then((ok) => { if (ok === false) { setCookie(null, LEGACY_COOKIE); reload(); } });
    }
    const q = new URLSearchParams(search).get("preview") ?? fromHash.get("preview");
    if (q !== null) {
      try { window.history.replaceState(null, "", window.location.pathname); } catch { /* the reload below cleans it */ }
    }
    if (q === "off") { setCookie(null); reload(); return; }
    if (q) {
      void checkCode(q).then((ok) => { if (ok === true) setCookie(q); reload(); });
      return;
    }
    const code = getCookie();
    if (code === null) return;
    addNoIndex();
    // Only a clear "not the current code" (the code was changed) removes it; a busy or failed check never drops someone out of the preview.
    void checkCode(code).then((ok) => { if (ok === false) { setCookie(null); reload(); } });
  } catch { /* never block start-up */ }
};

/** Dev/preview: `?public=next|legacy` (and `?home=W1|W2`) is remembered for the tab (see PreviewParams). */
export const isPublicNext = (): boolean => {
  const o = preview ? read("cy_public_preview") : null;
  if (o === "next" || o === "legacy") return o === "next";
  if (hasPublicLegacyCookie()) return false; // the old-public-pages link wins
  return (PUBLIC_MODE as PublicMode) === "next" || hasPublicPreviewCookie();
};

export const homeVariant = (): HomeVariant => {
  const o = preview ? read("cy_public_home") : null;
  return o === "W1" || o === "W2" ? o : HOME_VARIANT;
};

/** Called once at start-up: stores the preview switches from the address (dev/test builds only). */
export const capturePreviewParams = (search: string): void => {
  if (!preview) return;
  try {
    const q = new URLSearchParams(search);
    const p = q.get("public"); if (p === "next" || p === "legacy") window.sessionStorage.setItem("cy_public_preview", p);
    const h = q.get("home"); if (h === "W1" || h === "W2") window.sessionStorage.setItem("cy_public_home", h);
  } catch { /* storage blocked: the constants rule */ }
};
